import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { fetchPlayoffRounds, updatePlayoffMatch, updatePlayoffRound, recordGoldenSet } from '../api';

const PLAYOFF_TYPES = ['QF', 'SF', 'F', '3P', '5P', '56', '78'];
const PLAYOUT_TYPES = ['9P', 'TP', 'XP'];

const ROUND_TYPE_LABELS = {
  QF: 'Ćwierćfinały',
  SF: 'Półfinały',
  F: 'Finał',
  '3P': 'Mecz o 3. miejsce',
  '5P': 'Gra o miejsca 5-8',
  '56': 'Mecz o miejsca 5-6',
  '78': 'Mecz o miejsca 7-8',
  '9P': 'Gra o miejsca 9-12',
  TP: 'Mecz o miejsca 9-10',
  XP: 'Mecz o miejsca 11-12',
};

const PlayoffManagementPage = () => {
  const { seasonId } = useParams();
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Match edit dialog state
  const [editingMatch, setEditingMatch] = useState(null);
  const [matchForm, setMatchForm] = useState({
    home_score: 0,
    away_score: 0,
    match_date: '',
    match_time: '',
    match_completed: false,
  });

  // Golden set dialog state
  const [goldenSetDialog, setGoldenSetDialog] = useState(null); // {seriesId, team1, team2}
  const [goldenSetWinner, setGoldenSetWinner] = useState('');

  // Round date edit state
  const [savingRound, setSavingRound] = useState(null);
  const [roundDates, setRoundDates] = useState({});

  const loadRounds = async () => {
    try {
      setLoading(true);
      const response = await fetchPlayoffRounds(seasonId);
      const data = Array.isArray(response.data) ? response.data : [];
      setRounds(data);

      // Initialize round dates
      const dates = {};
      data.forEach((round) => {
        dates[round.id] = {
          start_date: round.start_date || '',
          end_date: round.end_date || '',
        };
      });
      setRoundDates(dates);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Nie udało się pobrać danych playoff.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonId]);

  const playoffRounds = useMemo(
    () => rounds.filter((r) => PLAYOFF_TYPES.includes(r.round_type)),
    [rounds]
  );
  const playoutRounds = useMemo(
    () => rounds.filter((r) => PLAYOUT_TYPES.includes(r.round_type)),
    [rounds]
  );

  const getSeriesStatus = (series) => {
    if (series.completed) return 'completed';
    const hasAnyScore = series.matches?.some(
      (m) => m.home_score > 0 || m.away_score > 0
    );
    return hasAnyScore ? 'in-progress' : 'pending';
  };

  const getStatusChip = (status) => {
    const map = {
      completed: { label: 'Zakończona', color: 'success' },
      'in-progress': { label: 'W trakcie', color: 'warning' },
      pending: { label: 'Oczekuje', color: 'default' },
    };
    const cfg = map[status] || map.pending;
    return <Chip label={cfg.label} color={cfg.color} size="small" />;
  };

  // --- Round date save ---
  const handleRoundDateChange = (roundId, field, value) => {
    setRoundDates((prev) => ({
      ...prev,
      [roundId]: { ...prev[roundId], [field]: value },
    }));
  };

  const handleSaveRoundDates = async (roundId) => {
    setSavingRound(roundId);
    setError('');
    setMessage('');
    try {
      await updatePlayoffRound(roundId, roundDates[roundId]);
      setMessage('Daty rundy zaktualizowane.');
      await loadRounds();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Nie udało się zapisać dat rundy.');
    } finally {
      setSavingRound(null);
    }
  };

  // --- Match edit ---
  const handleEditMatch = (match) => {
    setEditingMatch(match);
    setMatchForm({
      home_score: match.home_score,
      away_score: match.away_score,
      match_date: match.match_date || '',
      match_time: match.match_time || '',
      match_completed: match.match_completed,
    });
  };

  const validateScores = () => {
    const { home_score, away_score } = matchForm;
    const valid = [
      [0, 0], [3, 0], [3, 1], [3, 2], [0, 3], [1, 3], [2, 3],
    ];
    return valid.some(([h, a]) => h === home_score && a === away_score);
  };

  const handleSaveMatch = async () => {
    if (!validateScores()) {
      setError('Nieprawidłowy wynik. Dozwolone: 0:0, 3:0, 3:1, 3:2, 0:3, 1:3, 2:3.');
      return;
    }
    setError('');
    setMessage('');
    try {
      const response = await updatePlayoffMatch(editingMatch.id, matchForm);
      const msg = response?.data?.message || 'Mecz zaktualizowany.';
      setMessage(msg);
      setEditingMatch(null);
      await loadRounds();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Nie udało się zapisać meczu.');
    }
  };

  const handleOpenGoldenSet = (series) => {
    setGoldenSetDialog({
      seriesId: series.id,
      team1: series.team1,
      team2: series.team2,
    });
    setGoldenSetWinner('');
  };

  const handleRecordGoldenSet = async () => {
    if (!goldenSetWinner) {
      setError('Wybierz zwycięzcę złotego seta.');
      return;
    }
    setError('');
    setMessage('');
    try {
      const response = await recordGoldenSet({
        series_id: goldenSetDialog.seriesId,
        winner_team_id: goldenSetWinner,
      });
      setMessage(response?.data?.message || 'Złoty set zapisany.');
      setGoldenSetDialog(null);
      await loadRounds();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Nie udało się zapisać złotego seta.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderRoundGroup = (groupRounds, title) => {
    if (groupRounds.length === 0) return null;
    return (
      <>
        <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
          {title}
        </Typography>
        {groupRounds.map((round) => (
          <Paper key={round.id} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">
                {round.name} ({ROUND_TYPE_LABELS[round.round_type] || round.round_type})
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {round.start_date} — {round.end_date}
              </Typography>
            </Box>

            {/* Round date editor */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <TextField
                label="Data startu"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={roundDates[round.id]?.start_date || ''}
                onChange={(e) => handleRoundDateChange(round.id, 'start_date', e.target.value)}
              />
              <TextField
                label="Data końca"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={roundDates[round.id]?.end_date || ''}
                onChange={(e) => handleRoundDateChange(round.id, 'end_date', e.target.value)}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleSaveRoundDates(round.id)}
                disabled={savingRound === round.id}
              >
                {savingRound === round.id ? <CircularProgress size={16} /> : 'Zapisz daty'}
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Series & matches */}
            {round.series?.map((series) => {
              const isTbd = round.teams_tbd;
              // For TBD rounds, use the first match's description to label the series
              const seriesLabel = isTbd && series.matches?.[0]?.match_description
                ? series.matches[0].match_description.replace(/ \(I mecz\)$|\(Rewanż\)$/i, '').trim()
                : null;

              return (
                <Card
                  key={series.id}
                  variant="outlined"
                  sx={{ mb: 2, ...(isTbd ? { borderStyle: 'dashed', opacity: 0.85 } : {}) }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {isTbd
                          ? (seriesLabel || 'Do ustalenia')
                          : `${series.team1?.name || 'TBD'} vs ${series.team2?.name || 'TBD'}`
                        }
                      </Typography>
                      {isTbd && (
                        <Chip label="Do ustalenia" size="small" variant="outlined" color="info" />
                      )}
                      {!isTbd && getStatusChip(getSeriesStatus(series))}
                      {series.winner && (
                        <Chip
                          label={`Zwycięzca: ${series.winner.name}`}
                          color="primary"
                          size="small"
                        />
                      )}
                      {series.needs_golden_set && !series.golden_set_played && (
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          onClick={() => handleOpenGoldenSet(series)}
                        >
                          Zapisz złoty set
                        </Button>
                      )}
                      {series.golden_set_played && series.golden_set_winner && (
                        <Chip
                          label={`Złoty set: ${series.golden_set_winner.name}`}
                          color="warning"
                          size="small"
                        />
                      )}
                      {series.aggregate_score && (
                        <Typography variant="body2" color="text.secondary">
                          Aggregate: {series.aggregate_score.team1}-{series.aggregate_score.team2}
                          {series.golden_set_played ? ' (złoty set)' : ''}
                        </Typography>
                      )}
                    </Box>

                    {series.matches?.map((match) => (
                      <Box
                        key={match.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          py: 1,
                          borderBottom: '1px solid #eee',
                        }}
                      >
                        <Typography variant="body2" sx={{ minWidth: 60 }}>
                          {match.is_second_leg ? 'Rewanż' : 'I Mecz'}
                        </Typography>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {isTbd
                            ? (match.match_description || 'Do ustalenia')
                            : `${match.home_team?.name} ${match.home_score} : ${match.away_score} ${match.away_team?.name}`
                          }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {match.match_date} {match.match_time}
                        </Typography>
                        {match.match_completed && (
                          <Chip label="Zakończony" color="success" size="small" variant="outlined" />
                        )}
                        {!isTbd && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleEditMatch(match)}
                          >
                            Edytuj
                          </Button>
                        )}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </Paper>
        ))}
      </>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Drabinka Playoff
      </Typography>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {rounds.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography>
            Brak rund playoff dla tego sezonu. Użyj strony "Reguły i Playoffy" aby wygenerować drabinkę.
          </Typography>
        </Paper>
      ) : (
        <>
          {renderRoundGroup(playoffRounds, 'Play-off')}
          {renderRoundGroup(playoutRounds, 'Play-out')}
        </>
      )}

      {/* Golden Set Dialog */}
      <Dialog open={!!goldenSetDialog} onClose={() => setGoldenSetDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Złoty set — rozstrzygnięcie remisu</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Seria zakończyła się remisem 1-1 w meczach. Zgodnie z regulaminem rozgrywany jest złoty set
            (set decydujący). Wybierz drużynę, która wygrała złoty set.
          </Alert>
          <FormControl fullWidth>
            <InputLabel>Zwycięzca złotego seta</InputLabel>
            <Select
              value={goldenSetWinner}
              onChange={(e) => setGoldenSetWinner(e.target.value)}
              label="Zwycięzca złotego seta"
            >
              {goldenSetDialog && (
                [goldenSetDialog.team1, goldenSetDialog.team2].map((team) => (
                  <MenuItem key={team.id} value={team.id}>{team.name}</MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoldenSetDialog(null)}>Anuluj</Button>
          <Button variant="contained" color="warning" onClick={handleRecordGoldenSet}>
            Potwierdź
          </Button>
        </DialogActions>
      </Dialog>

      {/* Match Edit Dialog */}
      <Dialog open={!!editingMatch} onClose={() => setEditingMatch(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edytuj Mecz: {editingMatch?.home_team?.name} vs {editingMatch?.away_team?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Wynik gospodarzy</InputLabel>
              <Select
                value={matchForm.home_score}
                onChange={(e) => setMatchForm((f) => ({ ...f, home_score: e.target.value }))}
                label="Wynik gospodarzy"
              >
                {[0, 1, 2, 3].map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Wynik gości</InputLabel>
              <Select
                value={matchForm.away_score}
                onChange={(e) => setMatchForm((f) => ({ ...f, away_score: e.target.value }))}
                label="Wynik gości"
              >
                {[0, 1, 2, 3].map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="Data meczu"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={matchForm.match_date}
              onChange={(e) => setMatchForm((f) => ({ ...f, match_date: e.target.value }))}
            />
            <TextField
              label="Godzina"
              type="time"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={matchForm.match_time}
              onChange={(e) => setMatchForm((f) => ({ ...f, match_time: e.target.value }))}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={matchForm.match_completed}
                onChange={(e) => setMatchForm((f) => ({ ...f, match_completed: e.target.checked }))}
              />
            }
            label="Mecz zakończony"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingMatch(null)}>Anuluj</Button>
          <Button variant="contained" onClick={handleSaveMatch}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlayoffManagementPage;
